import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Send, Eye, RefreshCw, TestTube2, Users, Calendar } from "lucide-react";

const STORAGE_KEY = "mh_newsletter_draft_v1";

export default function Newsletter() {
  const [intro, setIntro] = useState("");
  const [outro, setOutro] = useState("");
  const [preview, setPreview] = useState(null); // {html, text, week, shows_count, recipients}
  const [busy, setBusy] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [sendingAll, setSendingAll] = useState(false);
  const [testEmail, setTestEmail] = useState("");

  // Load draft
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const d = JSON.parse(raw);
        setIntro(d.intro || "");
        setOutro(d.outro || "");
      }
    } catch { /* ignore */ }
  }, []);

  // Save draft on change
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ intro, outro })); } catch { /* ignore */ }
  }, [intro, outro]);

  const refresh = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/club/newsletter-preview", { intro, outro });
      setPreview(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Vorschau fehlgeschlagen");
    } finally { setBusy(false); }
  };

  useEffect(() => { refresh(); /* initial */ /* eslint-disable-next-line */ }, []);

  const sendTest = async () => {
    if (!testEmail || !testEmail.includes("@")) return toast.error("Gültige Test-E-Mail eingeben");
    setSendingTest(true);
    try {
      const { data } = await api.post("/club/newsletter-test", { email: testEmail, intro, outro });
      if (data?.sent) toast.success(`Test-Mail an ${testEmail} verschickt`);
      else toast.error("Test-Mail fehlgeschlagen");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Test-Mail fehlgeschlagen");
    } finally { setSendingTest(false); }
  };

  const sendAll = async () => {
    if (!window.confirm(`Newsletter an ${preview?.recipients ?? "?"} Mitglieder verschicken?`)) return;
    setSendingAll(true);
    try {
      const { data } = await api.post("/club/newsletter-run-now", { intro, outro });
      toast.success(`Verschickt · ${data.sent} erfolgreich · ${data.failed} fehlgeschlagen`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Versand fehlgeschlagen");
    } finally { setSendingAll(false); }
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto text-[#F5F0E6]">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] flex items-center gap-2"><Mail size={12} /> Club-Kommunikation</div>
          <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl leading-none mt-1">Newsletter</h1>
          <div className="text-xs text-[#A8A398] mt-2">Automatischer Wochen-Newsletter mit Programm + Custom Text — mit Live-Vorschau und Test-Versand.</div>
        </div>
        {preview && (
          <div className="flex items-center gap-5 text-xs text-[#A8A398]">
            <div className="flex items-center gap-2"><Calendar size={12} className="text-[#D4AF37]" /> {preview.week}</div>
            <div className="flex items-center gap-2"><Users size={12} className="text-[#D4AF37]" /> {preview.recipients} Mitglieder</div>
            <div>{preview.shows_count} Vorstellungen</div>
          </div>
        )}
      </div>

      <div className="mt-8 grid lg:grid-cols-[380px_1fr] gap-6">
        {/* Editor */}
        <aside className="card-lux p-5 md:p-6 space-y-5" data-testid="newsletter-editor">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Intro-Text (oberhalb der Vorstellungen)</label>
            <textarea rows={5}
              value={intro} onChange={(e) => setIntro(e.target.value)}
              data-testid="newsletter-intro"
              placeholder="z. B. spezielle Ankündigung, Willkommens-Nachricht, Event-Hinweis"
              className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 outline-none focus:border-[#D4AF37]" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Outro-Text (unterhalb der Vorstellungen)</label>
            <textarea rows={4}
              value={outro} onChange={(e) => setOutro(e.target.value)}
              data-testid="newsletter-outro"
              placeholder="z. B. Grüße, Hinweise, Snack-Angebot"
              className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 outline-none focus:border-[#D4AF37]" />
          </div>

          <div className="border-t border-[#1A1A1A] pt-4">
            <button onClick={refresh} disabled={busy} data-testid="newsletter-refresh"
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded border border-[#2A2A2A] hover:border-[#D4AF37] text-xs uppercase tracking-widest">
              <RefreshCw size={13} className={busy ? "animate-spin" : ""} /> Vorschau aktualisieren
            </button>
          </div>

          <div className="border-t border-[#1A1A1A] pt-4">
            <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Test-Versand</label>
            <div className="mt-1 flex gap-2">
              <input type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)}
                data-testid="newsletter-test-email"
                placeholder="test@example.com"
                className="flex-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 outline-none focus:border-[#D4AF37]" />
              <button onClick={sendTest} disabled={sendingTest || !testEmail}
                data-testid="newsletter-test-send"
                className="px-4 rounded border border-[#D4AF37] text-[#D4AF37] text-[10px] uppercase tracking-[0.28em] flex items-center gap-2 hover:bg-[#D4AF37]/10 disabled:opacity-40">
                <TestTube2 size={12} /> {sendingTest ? "…" : "Test"}
              </button>
            </div>
          </div>

          <div className="border-t border-[#1A1A1A] pt-4">
            <button onClick={sendAll} disabled={sendingAll || !preview?.recipients}
              data-testid="newsletter-send-all"
              className="w-full flex items-center justify-center gap-2 py-3 rounded-full bg-[#D4AF37] text-black text-sm uppercase tracking-widest hover:bg-[#EEDC82] disabled:opacity-40 disabled:cursor-not-allowed">
              <Send size={14} /> {sendingAll ? "Versende …" : `An ${preview?.recipients ?? 0} Mitglieder senden`}
            </button>
            <div className="text-[10px] text-[#575148] mt-2 leading-relaxed">
              Sendet an alle Club-Mitglieder mit E-Mail und aktivem Newsletter-Abo. Vorstellungen der nächsten 7 Tage werden automatisch angehängt.
            </div>
          </div>
        </aside>

        {/* Preview */}
        <section className="card-lux p-3 md:p-4">
          <div className="text-[10px] uppercase tracking-[0.35em] text-[#A8A398] flex items-center gap-2 px-2 pb-2">
            <Eye size={12} /> Live-Vorschau — so sehen Mitglieder die E-Mail
          </div>
          <div className="rounded overflow-hidden border border-[#1A1A1A] bg-white">
            {preview?.html ? (
              <iframe title="newsletter-preview" srcDoc={preview.html} className="w-full" style={{ minHeight: "72vh" }} data-testid="newsletter-preview-iframe" />
            ) : (
              <div className="p-10 text-center text-[#575148] text-sm">Vorschau lädt …</div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
